/** Which layout a device should be served. */
export type DeviceClass = "phone" | "tablet" | "desktop";

/** Matches the `mobile:` Tailwind variant in packages/subframe/ui/theme.css. */
export const PHONE_MEDIA_QUERY = "(max-width: 767px)";

/** The width half of the `tablet:` variant in packages/subframe/ui/theme.css. */
export const TABLET_WIDTH_MEDIA_QUERY =
  "(min-width: 768px) and (max-width: 1366px)";

export interface DeviceClassInput {
  /** Matches {@link PHONE_MEDIA_QUERY}. */
  isPhoneWidth: boolean;
  /** Matches {@link TABLET_WIDTH_MEDIA_QUERY}. */
  isTabletWidth: boolean;
  /** Whether the primary pointer is coarse — see `useIsTouchDevice`. */
  hasCoarsePointer: boolean;
}

/**
 * Classify the device so the shell can serve the in-room tablet layout to
 * iPads without disturbing phones or desktops (NAU-395).
 *
 * Inputs are media-query results rather than a pixel width so that every rule
 * here has a one-to-one counterpart in theme.css and the two cannot drift. It
 * also avoids `window.innerWidth`, which counts a classic scrollbar that a CSS
 * `max-width` query does not.
 *
 * Phone width wins outright, pointer ignored, so the phone path stays exactly
 * what the `mobile:` variant has always done. The 1366px ceiling is inclusive
 * to keep a 12.9" iPad Pro (1366px landscape) a tablet through rotation, and
 * bounds the coarse-pointer test so a touchscreen kiosk stays on desktop.
 */
export function resolveDeviceClass({
  isPhoneWidth,
  isTabletWidth,
  hasCoarsePointer,
}: DeviceClassInput): DeviceClass {
  if (isPhoneWidth) {
    return "phone";
  }

  if (hasCoarsePointer && isTabletWidth) {
    return "tablet";
  }

  return "desktop";
}
